import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';

// Static worker IP - must match backend meta.js
const WORKER_IPS = [
    { location: 'APAC', ip: '139.84.167.143' }
];

const WAF_TUTORIALS = [
    {
        id: 'cloudflare',
        name: 'Cloudflare',
        icon: '☁️',
        steps: [
            'Go to your Cloudflare dashboard → Security → WAF → Custom Rules',
            'Click "Create rule"',
            'Name: "Allow ALT Load Test"',
            'Under "If incoming requests match..." select "Edit expression"',
            'Enter: (ip.src in {139.84.167.143})',
            'Action: Skip → Select all options (Rate Limiting, WAF, etc.)',
            'Deploy the rule'
        ]
    },
    {
        id: 'aws-waf',
        name: 'AWS WAF',
        icon: '🔶',
        steps: [
            'Go to AWS WAF Console → IP Sets → Create IP set',
            'Name: "ALT-LoadTest-IPs"',
            'Add IP: 139.84.167.143/32',
            'Go to your Web ACL → Rules → Add rules → Add my own rules',
            'Rule type: IP set → Select your "ALT-LoadTest-IPs"',
            'Action: Allow',
            'Set rule priority to run before block rules'
        ]
    },
    {
        id: 'azure',
        name: 'Azure Front Door / WAF',
        icon: '🔵',
        steps: [
            'Go to Azure Portal → Your Front Door → WAF Policy',
            'Navigate to Custom rules → Add custom rule',
            'Name: "AllowALTLoadTest"',
            'Match type: IP address',
            'Match condition: Is in → Enter each IP on a new line',
            'Action: Allow',
            'Save and deploy the policy'
        ]
    },
    {
        id: 'nginx',
        name: 'Nginx (Rate Limiting)',
        icon: '🟢',
        steps: [
            'Add to your nginx.conf or site config:',
            'geo $is_loadtest {\n  default 0;\n  139.84.167.143 1;\n}',
            'Then in your rate limiting block:',
            'limit_req_zone $binary_remote_addr zone=api:10m rate=10r/s;',
            'Add: if ($is_loadtest) { set $limit_key ""; }',
            'Reload nginx: sudo nginx -s reload'
        ]
    },
    {
        id: 'akamai',
        name: 'Akamai',
        icon: '🌐',
        steps: [
            'Go to Akamai Control Center → Security → Application Security',
            'Select your security configuration',
            'Navigate to Network Lists → Create network list',
            'Name: "ALT-LoadTest-Allowlist"',
            'Add the IP to the list',
            'Go to Firewall Policies → Add exception',
            'Match on network list → Select your allowlist',
            'Action: Allow'
        ]
    }
];

const WafAllowlistGuide = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('cloudflare');
    const [copied, setCopied] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const allIps = WORKER_IPS.map(w => w.ip).join(', ');

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Only show for paid users
    if (!user?.hasPurchased) {
        return null;
    }

    return (
        <div className="waf-guide">
            <style>{`
        .waf-guide {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .waf-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          cursor: pointer;
        }

        .waf-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .waf-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
          margin-top: 4px;
        }

        .waf-toggle {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: var(--text-secondary);
          padding: 0;
        }

        .waf-content {
          margin-top: 20px;
        }

        .ip-list-section {
          background: var(--bg-secondary);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .ip-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .ip-list-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .copy-btn {
          background: var(--primary);
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .copy-btn:hover {
          opacity: 0.9;
        }

        .ip-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 8px;
        }

        .ip-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 6px;
        }

        .ip-location {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .ip-address {
          font-family: monospace;
          font-size: 13px;
          color: var(--text-primary);
          font-weight: 500;
        }

        .tutorial-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 16px;
          border-bottom: 1px solid var(--border-color);
          padding-bottom: 16px;
        }

        .tutorial-tab {
          padding: 8px 16px;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          background: var(--card-bg);
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .tutorial-tab:hover {
          border-color: var(--text-secondary);
        }

        .tutorial-tab.active {
          background: #6366f1;
          color: white;
          border-color: #6366f1;
        }

        .tutorial-content {
          background: var(--bg-secondary);
          border-radius: 8px;
          padding: 20px;
        }

        .tutorial-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tutorial-steps {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .tutorial-step {
          display: flex;
          gap: 12px;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border-color);
        }

        .tutorial-step:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .step-number {
          width: 24px;
          height: 24px;
          background: var(--primary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          flex-shrink: 0;
        }

        .step-text {
          font-size: 14px;
          color: var(--text-primary);
          line-height: 1.5;
          font-family: ${props => props.isCode ? 'monospace' : 'inherit'};
          white-space: pre-wrap;
          word-break: break-word;
        }

        .step-code {
          font-family: monospace;
          background: var(--bg-tertiary);
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          margin-top: 8px;
          overflow-x: auto;
          border: 1px solid var(--border-color);
        }

        @media (max-width: 768px) {
          .tutorial-tabs {
            justify-content: center;
          }

          .ip-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

            <div className="waf-header" onClick={() => setIsExpanded(!isExpanded)}>
                <div>
                    <h3 className="waf-title">🛡️ WAF Allowlisting Guide</h3>
                    <p className="waf-subtitle">
                        Configure your firewall to allow ALT load testing traffic
                    </p>
                </div>
                <button className="waf-toggle">
                    {isExpanded ? '▼' : '▶'}
                </button>
            </div>

            {isExpanded && (
                <div className="waf-content">
                    <div className="ip-list-section">
                        <div className="ip-list-header">
                            <span className="ip-list-title">ALT Worker IP Addresses</span>
                            <button
                                className="copy-btn"
                                onClick={() => copyToClipboard(allIps)}
                            >
                                {copied ? '✓ Copied!' : 'Copy All IPs'}
                            </button>
                        </div>
                        <div className="ip-grid">
                            {WORKER_IPS.map((worker, index) => (
                                <div key={index} className="ip-item">
                                    <div>
                                        <div className="ip-address">{worker.ip}</div>
                                        <div className="ip-location">{worker.location}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="tutorial-tabs">
                        {WAF_TUTORIALS.map(tutorial => (
                            <button
                                key={tutorial.id}
                                className={`tutorial-tab ${activeTab === tutorial.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(tutorial.id)}
                            >
                                <span>{tutorial.icon}</span>
                                {tutorial.name}
                            </button>
                        ))}
                    </div>

                    {WAF_TUTORIALS.filter(t => t.id === activeTab).map(tutorial => (
                        <div key={tutorial.id} className="tutorial-content">
                            <h4 className="tutorial-title">
                                <span>{tutorial.icon}</span>
                                {tutorial.name} Setup
                            </h4>
                            <ol className="tutorial-steps">
                                {tutorial.steps.map((step, index) => (
                                    <li key={index} className="tutorial-step">
                                        <span className="step-number">{index + 1}</span>
                                        <div className="step-text">
                                            {step.includes('\n') ? (
                                                <>
                                                    {step.split('\n')[0]}
                                                    <div className="step-code">
                                                        {step.split('\n').slice(1).join('\n')}
                                                    </div>
                                                </>
                                            ) : (
                                                step
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default WafAllowlistGuide;
